import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-body font-medium transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default: 'bg-accent text-text-on-brand hover:bg-accent-hover',
        secondary: 'bg-surface-sunken text-text-primary hover:bg-surface',
        outline: 'border border-border bg-bg text-text-primary hover:bg-surface',
        ghost: 'text-text-primary hover:bg-surface-sunken',
        destructive: 'bg-flagged text-text-on-flagged hover:opacity-90',
      },
      size: {
        // Note: `h-8` / `w-8` would resolve to 8px because the
        // Fibonacci spacing scale overrides `8 → 8px`. Use arbitrary
        // values to dodge the override and get a real button height.
        sm: 'h-[32px] px-13 text-body-sm',
        md: 'h-10 px-21',
        lg: 'h-12 px-34 text-h3',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  },
);

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, asChild, ...props },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  return <Comp ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />;
});

export { buttonVariants };
